<template>
  <div v-if="movies && movies.length">
    <div
      v-if="title || showFilter"
      class="justify-center q-pb-sm"
      :class="{
        'justify-around': title && showFilter,
        'column items-center': isMobileView && title && showFilter,
        'row items-end': !isMobileView && title && showFilter
      }"
    >
      <section-heading
        v-if="title"
        :heading-color="headingColor"
        :heading-color-hex="headingColorHex"
        :heading-font-size="headingFontSize"
        :heading-align="headingAlign"
        :heading-type="headingType"
        :heading-background-image-url="headingBackgroundImageUrl"
        :heading-background-image-repeat="headingBackgroundImageRepeat"
        :title="title"
        :is-mobile-view="isMobileView"
        :class="{
          'col': !isMobileView && showFilter && !setFullWidthHeading,
          'full-width': isMobileView || setFullWidthHeading,
          'text-white': lightText
        }"
      />

      <q-tabs
        v-if="showFilter"
        v-model="filterType"
        no-caps
        :dense="title && !isMobileView"
        align="center"
        class="q-pa-xs q-gutter-sm size-md text-subtitle text-dark col-auto q-mb-sm"
        active-color="primary"
        :class="{
          'text-white': lightText,
          'q-my-lg': !title
        }"
        @update:model-value="selectedTitleClassId = null; titleClassTabs = null"
      >
        <q-tab
          :key="'Now Playing'"
          :ripple="false"
          :name="'current'"
          :label="$t('movie.nowPlaying')"
        />

        <q-tab
          :key="'Coming Soon'"
          :ripple="false"
          :name="'upcoming'"
          :label="$t('movie.comingSoon')"
        />
      </q-tabs>
    </div>

    <div class="wrapper">
      <swiper
        v-if="movies"
        :key="changeCount"
        :modules="[Navigation, Pagination, Autoplay]"
        :navigation="{
          nextEl: `.swiper-button-next-${id}`,
          prevEl: `.swiper-button-prev-${id}`
        }"
        :pagination="{
          el: `.swiper-pagination-${id}`,
          type: 'bullets',
          clickable: true
        }"
        :autoplay="autoplayOptions"
        :slides-per-view="slidesPerView"
        :space-between="spacing"
        :breakpoints="isPhonePreview ? undefined : breakPoints"
        preload-images
        class="swiper"
      >
        <swiper-slide
          v-for="movie in movies"
          :key="movie.id"
          :style="{ 'padding-bottom': paginationVisible ? '40px' : undefined }"
        >
          <div
            class="wrapper cursor-pointer"
            @click.stop="selectMovie(movie)"
          >
            <explore-poster
              :movie="movie"
              :use-banner="useBanner"
              :border-radius="borderRadius"
              :shadow="shadow"
              :show-trailer-button-on-posters="showTrailerButton"
              :clean-image-on-posters="cleanImage"
              :explore-using-dark-mode="lightText"
              :show-movie-title-on-posters="showMovieTitle"
              :show-movie-rating="showMovieRating"
              :show-movie-genre="showMovieGenre"
              :font-size="fontSize"
              :has-text-wrapped="textWrapped"
              :has-text-centred="textCentred"
              :show-user-buttons="showUserButtons"
              :show-title-class="showTitleClass"
              :is-phone-preview="isPhonePreview"
              :show-coming-soon-date="showComingSoonDate"
            />
          </div>
        </swiper-slide>
        <div
          v-if="paginationVisible"
          class="swiper-pagination"
          :class="['swiper-pagination-' + id,
                   lightText ? 'text-white' : 'text-primary']"
        />
        <div
          v-if="controlsVisible"
          class="swiper-button-prev"
          :style="{
            'margin-top': paginationVisible ? '-40px' : undefined,
            'background-color': controlsBackgroundColor === 'custom' ? controlsBackgroundColorHex : undefined,
            'border-radius': `${borderRadius}px`,
            'margin-right': controlsBackgroundColor !== 'default' ? '0' : undefined
          }"
          :class="{
            ['bg-' + `${controlsBackgroundColor}`]: controlsBackgroundColor !== 'custom',
            ['swiper-button-prev-' + `${id}`]: controlsVisible,
            'text-primary': bgWhite,
            'text-white': !bgWhite,
            'shadow-5': !!controlsBackgroundColor && controlsBackgroundColor !== 'default'
          }"
        />
        <div
          v-if="controlsVisible"
          :id="'swiper-button-next-' + `${id}`"
          class="swiper-button-next"
          :style="{
            'margin-top': paginationVisible ? '-40px' : undefined,
            'background-color': controlsBackgroundColor === 'custom' ? controlsBackgroundColorHex : undefined,
            'border-radius': `${borderRadius}px`,
            'margin-right': controlsBackgroundColor !== 'default' ? '0' : undefined
          }"
          :class="{
            ['bg-' + `${controlsBackgroundColor}`]: controlsBackgroundColor !== 'custom',
            ['swiper-button-next-' + `${id}`]: controlsVisible,
            'text-primary': bgWhite,
            'text-white': !bgWhite,
            'shadow-5': !!controlsBackgroundColor && controlsBackgroundColor !== 'default'
          }"
        />
      </swiper>
    </div>
  </div>
</template>

<script>
import { TEXT_ALIGN_OPTIONS } from 'src/models/carousel/Carousel'
import { HEADING_TYPES } from 'src/models/page/Section'
import ExplorePoster from 'src/modules/consumer/components/ExplorePoster'
import SwiperModulesMixin from 'src/modules/consumer/components/mixins/SwiperModulesMixin'
import SectionHeading from 'src/modules/consumer/components/SectionHeading'
export default {
  name: 'MovieCarousel',
  components: {
    ExplorePoster,
    SectionHeading
  },
  mixins: [SwiperModulesMixin],
  props: {
    id: {
      type: Number,
      required: false,
      default: 0
    },
    type: {
      type: String,
      required: true,
      default: 'current',
      validator: function (value) {
        return ['current', 'upcoming', 'current-and-upcoming'].indexOf(value) !== -1
      }
    },
    titleClassIds: {
      type: Array,
      required: false,
      default: null
    },
    numberOfMoviesOnShow: {
      type: Number,
      required: false,
      default: null
    },
    showFilter: {
      type: Boolean,
      required: false,
      default: false
    },

    title: {
      type: String,
      required: false,
      default: null
    },
    spacing: {
      type: Number,
      required: false,
      default: 10
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 10
    },
    shadow: {
      type: Number,
      required: false,
      default: 0
    },
    useBanner: {
      type: Boolean,
      required: false,
      default: false
    },
    paginationVisible: {
      type: Boolean,
      required: false,
      default: true
    },
    controlsVisible: {
      type: Boolean,
      required: false,
      default: true
    },
    controlsBackgroundColor: {
      type: String,
      required: false,
      default: 'default'
    },
    controlsBackgroundColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    autoplay: {
      type: Boolean,
      required: false,
      default: false
    },
    delayInSeconds: {
      type: Number,
      required: false,
      default: 6
    },
    showMovieTitle: {
      type: Boolean,
      required: false,
      default: false
    },
    showMovieRating: {
      type: Boolean,
      required: false,
      default: false
    },
    showMovieGenre: {
      type: Boolean,
      required: false,
      default: false
    },
    showUserButtons: {
      type: Boolean,
      required: false,
      default: false
    },
    showTrailerButton: {
      type: Boolean,
      required: false,
      default: false
    },
    textWrapped: {
      type: Boolean,
      required: false,
      default: true
    },
    textCentred: {
      type: Boolean,
      required: false,
      default: true
    },
    lightText: {
      type: Boolean,
      required: false,
      default: false
    },
    fontSize: {
      type: Number,
      required: false,
      default: 20
    },
    cleanImage: {
      type: Boolean,
      required: false,
      default: false
    },
    showTitleClass: {
      type: Boolean,
      required: false,
      default: false
    },
    paginateSlidesGroup: {
      type: Boolean,
      required: false,
      default: false
    },
    hasCustomisedSlidesPerRow: {
      type: Boolean,
      required: false,
      default: false
    },
    slidesPerRow: {
      type: Number,
      required: false,
      default: 5.2
    },
    hasCustomisedMobileSlidesPerRow: {
      type: Boolean,
      required: false,
      default: false
    },
    mobileSlidesPerRow: {
      type: Number,
      required: false,
      default: 2.2
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    headingColor: {
      type: String,
      default: 'primary',
      required: false
    },
    headingColorHex: {
      type: String,
      default: '#FFFFFF',
      required: false
    },
    headingAlign: {
      type: String,
      default: null,
      required: false
    },
    headingFontSize: {
      type: String,
      default: null,
      required: false
    },
    headingType: {
      type: String,
      default: 'default',
      required: false
    },
    headingBackgroundImageUrl: {
      type: String,
      default: null,
      required: false
    },
    headingBackgroundImageRepeat: {
      type: Boolean,
      required: false,
      default: true
    },
    showComingSoonDate: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      changeCount: 0,
      filterType: this.showFilter ? 'current' : null
    }
  },
  computed: {
    unfilteredMovies () {
      if (this.type === 'current') {
        return _.orderBy(this.currentMovies.data, ['displayOrder' || 99, 'dateOfFirstShowing'], ['asc', 'desc']) // current movies set in latest showing first
      } else if (this.type === 'upcoming') {
        return _.sortBy(this.upcomingMovies.data, ['displayOrder' || 99, 'dateOfFirstShowing'])
      } else if (this.type === 'current-and-upcoming') {
        if (this.showFilter) {
          if (this.filterType === 'upcoming') return _.sortBy(this.upcomingMovies.data, ['displayOrder' || 99, 'dateOfFirstShowing'])
          return _.orderBy(this.currentMovies.data, ['displayOrder' || 99, 'dateOfFirstShowing'], ['asc', 'desc']) // current movies set in latest showing first
        }
        return _.sortBy(this.currentAndUpcomingMovies.data, 'magic')
      } else {
        throw new Error('Unhandled condition')
      }
    },
    movies () {
      if (_.isEmpty(this.titleClassIds)) {
        if (this.numberOfMoviesOnShow !== null && this.numberOfMoviesOnShow !== '') return this.unfilteredMovies.slice(0, this.numberOfMoviesOnShow)
        return this.unfilteredMovies
      } else {
        if (this.numberOfMoviesOnShow !== null && this.numberOfMoviesOnShow !== '') _.filter(this.unfilteredMovies.slice(0, this.numberOfMoviesOnShow), movie => this.titleClassIds.includes(movie.titleClassId))
        return _.filter(this.unfilteredMovies, movie => this.titleClassIds.includes(movie.titleClassId))
      }
    },
    breakPoints () {
      return this.useBanner
        ? {
            300: { slidesPerView: this.hasCustomisedMobileSlidesPerRow ? this.mobileSlidesPerRow : 1.2 },
            640: { slidesPerView: this.hasCustomisedSlidesPerRow ? this.slidesPerRow : 1.2 },
            720: { slidesPerView: this.hasCustomisedSlidesPerRow ? this.slidesPerRow : 2.2 },
            900: { slidesPerView: this.hasCustomisedSlidesPerRow ? this.slidesPerRow : 3.2 }
          }
        : {
            300: { slidesPerView: this.hasCustomisedMobileSlidesPerRow ? this.mobileSlidesPerRow : 2.2 },
            640: { slidesPerView: this.hasCustomisedSlidesPerRow ? this.slidesPerRow : 3.2 },
            720: { slidesPerView: this.hasCustomisedSlidesPerRow ? this.slidesPerRow : 4.2 },
            900: { slidesPerView: this.hasCustomisedSlidesPerRow ? this.slidesPerRow : 5.2 }
          }
    },
    slidesPerView () {
      if (this.isPhonePreview) {
        if (this.hasCustomisedMobileSlidesPerRow) return this.mobileSlidesPerRow
        if (this.useBanner) return 1.2
        return 2.2
      }
      return 1
    },
    bgWhite () {
      return this.controlsBackgroundColor === 'custom' && this.controlsBackgroundColorHex === '#ffffff'
    },
    setFullWidthHeading () {
      return this.headingType !== HEADING_TYPES.DEFAULT || (this.headingType === HEADING_TYPES.DEFAULT && this.headingAlign === TEXT_ALIGN_OPTIONS.CENTER)
    }
  },
  watch: {
    'slidesPerRow' () {
      this.changeCount++
    },
    'mobileSlidesPerRow' () {
      this.changeCount++
    },
    'spacing' () {
      this.changeCount++
    }
  },
  created () {
    _.extend(this, { HEADING_TYPES, TEXT_ALIGN_OPTIONS })
  },
  configuration: ['currentSite'],
  models: {
    currentMovies: {
      titleClassId: true
    },
    upcomingMovies: {
      titleClassId: true
    },
    currentAndUpcomingMovies: {
      titleClassId: true
    }
  },
  methods: {
    selectMovie (movie) {
      this.$trackEvent(this.$customerEvents.CLICKED_MOVIE, movie.trackingData)
      this.$router.push(movie.pagePath)
    }
  }
}
</script>
<style lang="scss" scoped>

  .swiper {
    width: 100%;
    padding-left: 8px;
    padding-right: 8px;
    overflow: reset;
  }
  .swiper-button-prev, .swiper-button-next {
    color: rgba(333, 333, 333, 0.2);
  }

  .swiper-wrapper {
    justify-content: center;
  }

  .swiper-slide .wrapper {
    width: 100%;
  }
</style>
